/**
 * Create Centralized Agent Wizard (Phase 3b)
 *
 * Multi-step modal that walks a user through creating a new Ziro centralized
 * agent end-to-end. The wizard enforces the 11-item mandatory registration
 * rule from AGENT-DASHBOARD-REFORM.md — the "Create" button at the end is
 * disabled until every required field is filled.
 *
 * v1 supports two patterns:
 *   - Tier 1 Specialist Wrapper (single LLM call)
 *   - Tier 3 Multi-Action LangGraph Agent
 *
 * Deferred to v2: Tier 2 structured output agents, CrewAI crews, the 3
 * specialized creation flows (External-from-Internal, Specialized-from-
 * Standardized, Crew Variant), and inline workflow-graph editing.
 */

import { useState, useMemo, type ReactNode } from 'react'
import toast from 'react-hot-toast'
import Modal from '@/components/common/Modal'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import { apiClient } from '@/services/api'
import {
  CheckCircleIcon,
  XCircleIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'

// ── Types ─────────────────────────────────────────────────────

interface Action {
  name: string
  description: string
  prompt_additions: string
}

interface CrewStage {
  name: string
  description: string
  prompt: string
  temperature: number
}

interface WizardState {
  // Step 1: taxonomy
  tier: 1 | 2 | 3
  agent_type: 'internal' | 'external'
  variant: 'standardized' | 'specialized'
  supported_modalities: Array<'text' | 'voice' | 'video' | 'image'>

  // Step 2: pattern
  pattern: 'tier1_specialist' | 'tier2_specialist' | 'tier3_langgraph' | 'tier3_crew'

  // Tier 2 only
  output_schema: string

  // Step 3: identity
  name: string
  display_name: string
  description: string
  domain: string
  version: string

  // Step 4: prompt + routing
  system_prompt: string
  specialist_type: string

  // Step 5: tools
  tools: string[]

  // Step 6: architecture
  architectures: string[]

  // Step 7: detailed docs
  docs_summary: string
  docs_input_description: string
  docs_input_example: string
  docs_output_description: string
  docs_output_schema: string
  docs_use_cases: string[]
  docs_constraints: string[]

  // Step 8: tier 3 multi-action routing
  actions: Action[]

  // Step 8: tier 3 crew stages
  stages: CrewStage[]
}

const DEFAULT_STATE: WizardState = {
  tier: 1,
  agent_type: 'internal',
  variant: 'specialized',
  supported_modalities: ['text'],
  pattern: 'tier1_specialist',
  output_schema: '',
  name: '',
  display_name: '',
  description: '',
  domain: 'general',
  version: '1.0.0',
  system_prompt: '',
  specialist_type: 'general',
  tools: ['LLM (UnifiedLLMProvider)'],
  architectures: ['User-Created Agents'],
  docs_summary: '',
  docs_input_description: '',
  docs_input_example: '',
  docs_output_description: '',
  docs_output_schema: '',
  docs_use_cases: ['', '', ''],
  docs_constraints: [],
  actions: [],
  stages: [],
}

// ── Step configuration ────────────────────────────────────────

const STEPS = [
  { id: 1, name: 'Taxonomy', description: 'Tier, type, variant, modalities' },
  { id: 2, name: 'Pattern', description: 'Code generation template' },
  { id: 3, name: 'Identity', description: 'Name, description, domain' },
  { id: 4, name: 'Prompt', description: 'System prompt + routing hint' },
  { id: 5, name: 'Tools', description: 'Tools declaration' },
  { id: 6, name: 'Architecture', description: 'Architecture membership' },
  { id: 7, name: 'Documentation', description: 'Summary, input, output, use cases' },
  { id: 8, name: 'Actions', description: 'Tier 3 action routing (if applicable)' },
  { id: 9, name: 'Review', description: 'Checklist + submit' },
] as const

// ── Props ─────────────────────────────────────────────────────

interface CreateCentralizedAgentWizardProps {
  isOpen: boolean
  onClose: () => void
  onCreated?: (name: string) => void
}

// ═══════════════════════════════════════════════════════════════
//  Component
// ═══════════════════════════════════════════════════════════════

export default function CreateCentralizedAgentWizard({
  isOpen,
  onClose,
  onCreated,
}: CreateCentralizedAgentWizardProps) {
  const [step, setStep] = useState(1)
  const [state, setState] = useState<WizardState>(DEFAULT_STATE)
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<any>(null)

  // ── Derived: rule enforcement ──
  const invalidStandardizedInternal =
    state.variant === 'standardized' && state.agent_type === 'internal'

  // ── Derived: per-step validity ──
  const stepValidity = useMemo<Record<number, boolean>>(() => {
    const v: Record<number, boolean> = {}
    v[1] = !invalidStandardizedInternal && state.supported_modalities.length > 0

    // Pattern must match tier; Tier 2 additionally requires an output schema
    const patternMatchesTier =
      (state.tier === 1 && state.pattern === 'tier1_specialist') ||
      (state.tier === 2 && state.pattern === 'tier2_specialist') ||
      (state.tier === 3 &&
        (state.pattern === 'tier3_langgraph' || state.pattern === 'tier3_crew'))
    const tier2HasSchema =
      state.pattern !== 'tier2_specialist' || state.output_schema.trim().length > 0
    v[2] = patternMatchesTier && tier2HasSchema

    v[3] =
      /^[a-z][a-z0-9-]*[a-z0-9]$/.test(state.name) &&
      state.name.length >= 3 &&
      state.display_name.trim().length > 0 &&
      state.description.trim().length >= 10 &&
      state.domain.trim().length > 0 &&
      /^\d+\.\d+\.\d+$/.test(state.version)
    v[4] = state.system_prompt.trim().length >= 20
    v[5] = state.tools.length > 0 && state.tools.every((t) => t.trim().length > 0)
    v[6] = state.architectures.length > 0 && state.architectures.every((a) => a.trim().length > 0)
    v[7] =
      state.docs_summary.trim().length >= 10 &&
      state.docs_input_description.trim().length > 0 &&
      state.docs_input_example.trim().length > 0 &&
      state.docs_output_description.trim().length > 0 &&
      state.docs_output_schema.trim().length > 0 &&
      state.docs_use_cases.filter((u) => u.trim().length > 0).length >= 3

    // Step 8 depends on which Tier 3 pattern was picked
    if (state.pattern === 'tier3_langgraph') {
      v[8] =
        state.actions.length > 0 &&
        state.actions.every(
          (a) =>
            /^[a-z][a-z0-9_]*$/.test(a.name) && a.description.trim().length > 0,
        )
    } else if (state.pattern === 'tier3_crew') {
      v[8] =
        state.stages.length > 0 &&
        state.stages.every(
          (s) =>
            /^[a-z][a-z0-9_]*$/.test(s.name) &&
            s.prompt.trim().length >= 10 &&
            s.temperature >= 0 &&
            s.temperature <= 2,
        )
    } else {
      // Tier 1/2 don't need step 8
      v[8] = true
    }

    v[9] = [1, 2, 3, 4, 5, 6, 7, 8].every((s) => v[s])
    return v
  }, [state, invalidStandardizedInternal])

  const allValid = stepValidity[9]

  // ── Handlers ──

  const updateState = (patch: Partial<WizardState>) => {
    setState((prev) => ({ ...prev, ...patch }))
  }

  const handleSubmit = async () => {
    if (!allValid) {
      toast.error('Please complete all required fields before submitting')
      return
    }
    setSubmitting(true)
    try {
      const payload = {
        name: state.name,
        display_name: state.display_name,
        description: state.description,
        domain: state.domain,
        version: state.version,
        tier: state.tier,
        agent_type: state.agent_type,
        variant: state.variant,
        supported_modalities: state.supported_modalities,
        pattern: state.pattern,
        system_prompt: state.system_prompt,
        specialist_type: state.specialist_type,
        tools: state.tools,
        architectures: state.architectures,
        detailed_docs: {
          summary: state.docs_summary,
          input: {
            description: state.docs_input_description,
            example: state.docs_input_example,
          },
          output: {
            description: state.docs_output_description,
            schema: state.docs_output_schema,
          },
          use_cases: state.docs_use_cases.filter((u) => u.trim().length > 0),
          constraints: state.docs_constraints.filter((c) => c.trim().length > 0),
        },
        actions:
          state.pattern === 'tier3_langgraph'
            ? state.actions.map((a) => ({
                name: a.name,
                description: a.description,
                prompt_additions: a.prompt_additions,
              }))
            : undefined,
        stages:
          state.pattern === 'tier3_crew'
            ? state.stages.map((s) => ({
                name: s.name,
                description: s.description,
                prompt: s.prompt,
                temperature: s.temperature,
              }))
            : undefined,
        output_schema:
          state.pattern === 'tier2_specialist' ? state.output_schema : undefined,
      }
      const res = await apiClient.createCentralizedAgent(payload)
      setResult(res)
      toast.success('Agent created! Restart the backend to activate it.')
      if (onCreated) onCreated(res.name)
    } catch (e: any) {
      const detail = e?.response?.data?.detail
      const msg = typeof detail === 'string' ? detail : detail?.error || e.message
      toast.error(`Failed to create agent: ${msg}`)
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    if (submitting) return
    setStep(1)
    setState(DEFAULT_STATE)
    setResult(null)
    onClose()
  }

  // ── Render ──

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create Centralized Ziro Agent"
      description="Walk through 9 steps to create, register, and document a new agent."
      size="4xl"
    >
      {result ? (
        <SuccessView result={result} onClose={handleClose} />
      ) : (
        <>
          <Stepper steps={STEPS} currentStep={step} validity={stepValidity} onJump={setStep} />

          <div className="mt-6 min-h-[320px]">
            {step === 1 && <Step1Taxonomy state={state} update={updateState} />}
            {step === 2 && <Step2Pattern state={state} update={updateState} />}
            {step === 3 && <Step3Identity state={state} update={updateState} />}
            {step === 4 && <Step4Prompt state={state} update={updateState} />}
            {step === 5 && <Step5Tools state={state} update={updateState} />}
            {step === 6 && <Step6Architecture state={state} update={updateState} />}
            {step === 7 && <Step7Docs state={state} update={updateState} />}
            {step === 8 && <Step8Actions state={state} update={updateState} />}
            {step === 9 && <Step9Review state={state} validity={stepValidity} />}
          </div>

          <div className="mt-6 flex items-center justify-between border-t border-slate-200 pt-4 dark:border-slate-700">
            <Button
              variant="secondary"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1 || submitting}
            >
              <ChevronLeftIcon className="mr-1 h-4 w-4" /> Back
            </Button>

            <div className="text-sm text-slate-500 dark:text-slate-400">
              Step {step} of {STEPS.length}
            </div>

            {step < STEPS.length ? (
              <Button
                onClick={() => setStep((s) => Math.min(STEPS.length, s + 1))}
                disabled={!stepValidity[step] || submitting}
              >
                Next <ChevronRightIcon className="ml-1 h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={!allValid || submitting}
                isLoading={submitting}
              >
                Create Agent
              </Button>
            )}
          </div>
        </>
      )}
    </Modal>
  )
}

// ═══════════════════════════════════════════════════════════════
//  Sub-components
// ═══════════════════════════════════════════════════════════════

function Stepper({
  steps,
  currentStep,
  validity,
  onJump,
}: {
  steps: readonly { id: number; name: string; description: string }[]
  currentStep: number
  validity: Record<number, boolean>
  onJump: (n: number) => void
}) {
  return (
    <div className="flex gap-1 overflow-x-auto">
      {steps.map((s) => {
        const done = validity[s.id]
        const active = s.id === currentStep
        return (
          <button
            key={s.id}
            onClick={() => onJump(s.id)}
            className={clsx(
              'flex-1 min-w-[100px] rounded border-b-2 px-2 py-2 text-left transition-colors',
              active
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/40'
                : done
                ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/20'
                : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800',
            )}
          >
            <div className="flex items-center gap-1 text-xs font-semibold">
              {done ? (
                <CheckCircleIcon className="h-4 w-4 text-emerald-600" />
              ) : (
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-slate-300 text-[10px] text-white dark:bg-slate-600">
                  {s.id}
                </span>
              )}
              <span className="truncate">{s.name}</span>
            </div>
          </button>
        )
      })}
    </div>
  )
}

// ── Step 1: Taxonomy ──

function Step1Taxonomy({
  state,
  update,
}: {
  state: WizardState
  update: (patch: Partial<WizardState>) => void
}) {
  const invalidCombo = state.variant === 'standardized' && state.agent_type === 'internal'
  return (
    <div className="space-y-5">
      <Section title="Tier" help="How sophisticated is the execution pipeline?">
        <RadioGroup
          options={[
            { value: 1, label: 'Tier 1', hint: 'Single LLM call (stateless specialist)' },
            { value: 2, label: 'Tier 2', hint: 'Structured JSON output with schema' },
            { value: 3, label: 'Tier 3', hint: 'Multi-step, action routing, or crew pipeline' },
          ]}
          value={state.tier}
          onChange={(v) => {
            const newTier = v as 1 | 2 | 3
            // Reset pattern to the canonical choice for that tier
            const newPattern =
              newTier === 1
                ? 'tier1_specialist'
                : newTier === 2
                ? 'tier2_specialist'
                : 'tier3_langgraph'
            update({ tier: newTier, pattern: newPattern })
          }}
        />
      </Section>

      <Section title="Type" help="Is it tied to Ziro infrastructure?">
        <RadioGroup
          options={[
            { value: 'internal', label: 'Internal', hint: 'Bound to Ziro (NeuralGraph, internal APIs)' },
            { value: 'external', label: 'External', hint: 'No Ziro bindings, portable' },
          ]}
          value={state.agent_type}
          onChange={(v) => update({ agent_type: v as 'internal' | 'external' })}
        />
      </Section>

      <Section title="Variant" help="Is it bound to a specific platform?">
        <RadioGroup
          options={[
            { value: 'specialized', label: 'Specialized', hint: 'Tied to a concrete platform' },
            { value: 'standardized', label: 'Standardized', hint: 'Generic, reusable blueprint (always External)' },
          ]}
          value={state.variant}
          onChange={(v) =>
            update({
              variant: v as 'standardized' | 'specialized',
              // enforce: standardized → external
              agent_type: v === 'standardized' ? 'external' : state.agent_type,
            })
          }
        />
      </Section>

      {invalidCombo && (
        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
          Invalid combination: Standardized agents must be External. Change one of them.
        </div>
      )}

      <Section title="Supported modalities" help="What input types does this agent handle?">
        <div className="flex flex-wrap gap-2">
          {(['text', 'voice', 'video', 'image'] as const).map((m) => {
            const active = state.supported_modalities.includes(m)
            return (
              <button
                key={m}
                onClick={() =>
                  update({
                    supported_modalities: active
                      ? state.supported_modalities.filter((x) => x !== m)
                      : [...state.supported_modalities, m],
                  })
                }
                className={clsx(
                  'rounded-full border px-3 py-1 text-xs font-medium capitalize',
                  active
                    ? 'border-blue-500 bg-blue-500 text-white'
                    : 'border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300',
                )}
              >
                {m}
              </button>
            )
          })}
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Multi-modal execution (voice/video/image) requires Phase 5 infrastructure. For now only
          &quot;text&quot; agents run end-to-end; others will error until Phase 5 is deployed.
        </p>
      </Section>
    </div>
  )
}

// ── Step 2: Pattern ──

function Step2Pattern({
  state,
  update,
}: {
  state: WizardState
  update: (patch: Partial<WizardState>) => void
}) {
  const patternOptions = [
    {
      value: 'tier1_specialist',
      label: 'Tier 1 Specialist Wrapper',
      hint: 'Single LLM call with a pre-baked system prompt',
      disabled: state.tier !== 1,
    },
    {
      value: 'tier2_specialist',
      label: 'Tier 2 Structured-Output Specialist',
      hint: 'Single LLM call that produces JSON matching a schema',
      disabled: state.tier !== 2,
    },
    {
      value: 'tier3_langgraph',
      label: 'Tier 3 Multi-Action LangGraph Agent',
      hint: 'Custom state + action routing + per-action handlers',
      disabled: state.tier !== 3,
    },
    {
      value: 'tier3_crew',
      label: 'Tier 3 Multi-Stage Crew',
      hint: 'Sequential pipeline with per-stage prompts + temperatures',
      disabled: state.tier !== 3,
    },
  ]
  return (
    <div className="space-y-4">
      <Section title="Code generation pattern" help="The skeleton template to generate">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {patternOptions.map((o) => {
            const active = o.value === state.pattern
            return (
              <button
                key={o.value}
                onClick={() => !o.disabled && update({ pattern: o.value as WizardState['pattern'] })}
                disabled={o.disabled}
                className={clsx(
                  'rounded-md border p-3 text-left transition-colors',
                  o.disabled && 'cursor-not-allowed opacity-50',
                  active
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/40'
                    : 'border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-800',
                )}
              >
                <div className="text-sm font-semibold">{o.label}</div>
                <div className="mt-1 text-xs text-slate-500">{o.hint}</div>
              </button>
            )
          })}
        </div>
      </Section>

      {state.pattern === 'tier2_specialist' && (
        <Section
          title="Output schema (JSON)"
          help="The JSON structure the LLM must produce. Embedded into the system prompt."
        >
          <textarea
            rows={6}
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 font-mono text-xs dark:border-slate-600 dark:bg-slate-800"
            value={state.output_schema}
            onChange={(e) => update({ output_schema: e.target.value })}
            placeholder={'{\n  "entities": [\n    {"name": "string", "type": "person|org|place", "offset": 0}\n  ]\n}'}
          />
        </Section>
      )}

      <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
        Deferred to a later iteration: External-from-Internal flow,
        Specialized-from-Standardized flow, and hot reload. Backend restart
        still required for new agents to become active.
      </div>
    </div>
  )
}

// ── Step 3: Identity ──

function Step3Identity({
  state,
  update,
}: {
  state: WizardState
  update: (patch: Partial<WizardState>) => void
}) {
  const nameValid =
    state.name.length === 0 ||
    (/^[a-z][a-z0-9-]*[a-z0-9]$/.test(state.name) && state.name.length >= 3)
  return (
    <div className="space-y-4">
      <Input
        label="Agent name (hyphen-case, lowercase)"
        placeholder="my-new-agent"
        value={state.name}
        onChange={(e) => update({ name: e.target.value })}
        error={!nameValid ? 'Must be lowercase, 3+ chars, hyphens only' : undefined}
      />
      <Input
        label="Display name"
        placeholder="My New Agent"
        value={state.display_name}
        onChange={(e) => update({ display_name: e.target.value })}
      />
      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
          Description (10+ characters)
        </label>
        <textarea
          className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800"
          rows={3}
          placeholder="A one-line summary of what the agent does"
          value={state.description}
          onChange={(e) => update({ description: e.target.value })}
        />
      </div>
      <Input
        label="Domain (e.g. documents, coding, analysis)"
        value={state.domain}
        onChange={(e) => update({ domain: e.target.value })}
      />
      <Input
        label="Version (semver)"
        placeholder="1.0.0"
        value={state.version}
        onChange={(e) => update({ version: e.target.value })}
      />
    </div>
  )
}

// ── Step 4: Prompt ──

function Step4Prompt({
  state,
  update,
}: {
  state: WizardState
  update: (patch: Partial<WizardState>) => void
}) {
  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
          System prompt (20+ characters)
        </label>
        <textarea
          className="h-48 w-full rounded-md border border-slate-300 bg-white px-3 py-2 font-mono text-sm dark:border-slate-600 dark:bg-slate-800"
          placeholder="You are a specialist assistant who..."
          value={state.system_prompt}
          onChange={(e) => update({ system_prompt: e.target.value })}
        />
        <p className="mt-1 text-xs text-slate-500">
          Used as the opening message for every invocation. Keep it focused on ONE task.
        </p>
      </div>
      <Input
        label="Specialist type (provider routing hint)"
        value={state.specialist_type}
        onChange={(e) => update({ specialist_type: e.target.value })}
        placeholder="general, coding, documents, analysis, ..."
      />
    </div>
  )
}

// ── Step 5: Tools ──

function Step5Tools({
  state,
  update,
}: {
  state: WizardState
  update: (patch: Partial<WizardState>) => void
}) {
  const addTool = () => update({ tools: [...state.tools, ''] })
  const removeTool = (i: number) =>
    update({ tools: state.tools.filter((_, idx) => idx !== i) })
  const updateTool = (i: number, v: string) =>
    update({ tools: state.tools.map((t, idx) => (idx === i ? v : t)) })
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600 dark:text-slate-400">
        Declare every tool or service the agent depends on. At minimum, include{' '}
        <code className="rounded bg-slate-100 px-1 py-0.5 text-xs dark:bg-slate-800">
          LLM (UnifiedLLMProvider)
        </code>
        .
      </p>
      {state.tools.map((t, i) => (
        <div key={i} className="flex items-end gap-2">
          <div className="flex-1">
            <Input
              value={t}
              onChange={(e) => updateTool(i, e.target.value)}
              placeholder="e.g. Google Drive API (OAuth)"
            />
          </div>
          <Button variant="ghost" onClick={() => removeTool(i)} disabled={state.tools.length === 1}>
            <TrashIcon className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button variant="secondary" onClick={addTool}>
        <PlusIcon className="mr-1 h-4 w-4" /> Add tool
      </Button>
    </div>
  )
}

// ── Step 6: Architecture ──

function Step6Architecture({
  state,
  update,
}: {
  state: WizardState
  update: (patch: Partial<WizardState>) => void
}) {
  const addArch = () => update({ architectures: [...state.architectures, ''] })
  const removeArch = (i: number) =>
    update({ architectures: state.architectures.filter((_, idx) => idx !== i) })
  const updateArch = (i: number, v: string) =>
    update({ architectures: state.architectures.map((a, idx) => (idx === i ? v : a)) })
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600 dark:text-slate-400">
        Every agent must belong to at least one architecture. Use an existing architecture name
        (e.g. &quot;Ziro Intelligence&quot;) or pick the default{' '}
        <code className="rounded bg-slate-100 px-1 py-0.5 text-xs dark:bg-slate-800">
          User-Created Agents
        </code>
        .
      </p>
      {state.architectures.map((a, i) => (
        <div key={i} className="flex items-end gap-2">
          <div className="flex-1">
            <Input
              value={a}
              onChange={(e) => updateArch(i, e.target.value)}
              placeholder="User-Created Agents"
            />
          </div>
          <Button
            variant="ghost"
            onClick={() => removeArch(i)}
            disabled={state.architectures.length === 1}
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button variant="secondary" onClick={addArch}>
        <PlusIcon className="mr-1 h-4 w-4" /> Add architecture
      </Button>
    </div>
  )
}

// ── Step 7: Detailed Docs ──

function Step7Docs({
  state,
  update,
}: {
  state: WizardState
  update: (patch: Partial<WizardState>) => void
}) {
  const updateUseCase = (i: number, v: string) =>
    update({ docs_use_cases: state.docs_use_cases.map((u, idx) => (idx === i ? v : u)) })
  const addUseCase = () => update({ docs_use_cases: [...state.docs_use_cases, ''] })
  const removeUseCase = (i: number) =>
    update({ docs_use_cases: state.docs_use_cases.filter((_, idx) => idx !== i) })
  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1 block text-sm font-medium">Summary (one paragraph)</label>
        <textarea
          rows={3}
          className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800"
          value={state.docs_summary}
          onChange={(e) => update({ docs_summary: e.target.value })}
          placeholder="What does this agent do, in one paragraph?"
        />
      </div>
      <Input
        label="Input description"
        value={state.docs_input_description}
        onChange={(e) => update({ docs_input_description: e.target.value })}
      />
      <div>
        <label className="mb-1 block text-sm font-medium">Input example</label>
        <textarea
          rows={2}
          className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 font-mono text-xs dark:border-slate-600 dark:bg-slate-800"
          value={state.docs_input_example}
          onChange={(e) => update({ docs_input_example: e.target.value })}
          placeholder={'Prompt: "Do thing"\\nContext: { "key": "value" }'}
        />
      </div>
      <Input
        label="Output description"
        value={state.docs_output_description}
        onChange={(e) => update({ docs_output_description: e.target.value })}
      />
      <div>
        <label className="mb-1 block text-sm font-medium">Output schema (JSON string)</label>
        <textarea
          rows={3}
          className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 font-mono text-xs dark:border-slate-600 dark:bg-slate-800"
          value={state.docs_output_schema}
          onChange={(e) => update({ docs_output_schema: e.target.value })}
          placeholder={'{ "result": { "success": true, "output": "string" } }'}
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium">Use cases (at least 3)</label>
        {state.docs_use_cases.map((u, i) => (
          <div key={i} className="mb-2 flex items-end gap-2">
            <div className="flex-1">
              <Input
                value={u}
                onChange={(e) => updateUseCase(i, e.target.value)}
                placeholder={`Use case ${i + 1}`}
              />
            </div>
            <Button
              variant="ghost"
              onClick={() => removeUseCase(i)}
              disabled={state.docs_use_cases.length <= 3}
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button variant="secondary" onClick={addUseCase}>
          <PlusIcon className="mr-1 h-4 w-4" /> Add use case
        </Button>
      </div>
    </div>
  )
}

// ── Step 8: Actions (Tier 3 only) ──

function Step8Actions({
  state,
  update,
}: {
  state: WizardState
  update: (patch: Partial<WizardState>) => void
}) {
  // Branch based on which Tier 3 pattern the user chose
  if (state.pattern === 'tier3_crew') {
    return <Step8CrewStages state={state} update={update} />
  }
  if (state.pattern !== 'tier3_langgraph') {
    return (
      <div className="rounded-md bg-slate-50 p-4 text-sm text-slate-600 dark:bg-slate-800 dark:text-slate-400">
        Tier 1 and Tier 2 specialists don&apos;t have per-action or per-stage configuration. Skip
        to the review step.
      </div>
    )
  }
  const addAction = () =>
    update({
      actions: [...state.actions, { name: '', description: '', prompt_additions: '' }],
    })
  const removeAction = (i: number) =>
    update({ actions: state.actions.filter((_, idx) => idx !== i) })
  const updateAction = (i: number, patch: Partial<Action>) =>
    update({
      actions: state.actions.map((a, idx) => (idx === i ? { ...a, ...patch } : a)),
    })
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600 dark:text-slate-400">
        Each action becomes a{' '}
        <code className="rounded bg-slate-100 px-1 py-0.5 text-xs dark:bg-slate-800">
          _stage_&lt;action&gt;
        </code>{' '}
        method on the generated class. Invoke with{' '}
        <code className="rounded bg-slate-100 px-1 py-0.5 text-xs dark:bg-slate-800">
          context.action
        </code>
        .
      </p>
      {state.actions.map((a, i) => (
        <div
          key={i}
          className="space-y-2 rounded-md border border-slate-200 p-3 dark:border-slate-700"
        >
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Input
                label="Action name (snake_case)"
                value={a.name}
                onChange={(e) => updateAction(i, { name: e.target.value })}
                placeholder="analyze"
              />
            </div>
            <Button variant="ghost" onClick={() => removeAction(i)}>
              <TrashIcon className="h-4 w-4" />
            </Button>
          </div>
          <Input
            label="Description"
            value={a.description}
            onChange={(e) => updateAction(i, { description: e.target.value })}
          />
          <div>
            <label className="mb-1 block text-sm font-medium">Prompt additions (optional)</label>
            <textarea
              rows={2}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 font-mono text-xs dark:border-slate-600 dark:bg-slate-800"
              value={a.prompt_additions}
              onChange={(e) => updateAction(i, { prompt_additions: e.target.value })}
              placeholder="Extra instructions appended to the system prompt when this action runs"
            />
          </div>
        </div>
      ))}
      <Button variant="secondary" onClick={addAction}>
        <PlusIcon className="mr-1 h-4 w-4" /> Add action
      </Button>
    </div>
  )
}

function Step8CrewStages({
  state,
  update,
}: {
  state: WizardState
  update: (patch: Partial<WizardState>) => void
}) {
  const addStage = () =>
    update({
      stages: [
        ...state.stages,
        { name: '', description: '', prompt: '', temperature: 0.3 },
      ],
    })
  const removeStage = (i: number) =>
    update({ stages: state.stages.filter((_, idx) => idx !== i) })
  const updateStage = (i: number, patch: Partial<CrewStage>) =>
    update({
      stages: state.stages.map((s, idx) => (idx === i ? { ...s, ...patch } : s)),
    })
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600 dark:text-slate-400">
        Crew stages run sequentially; each stage&apos;s output is piped into the next as{' '}
        <code className="rounded bg-slate-100 px-1 py-0.5 text-xs dark:bg-slate-800">
          current_input
        </code>
        . Set the system prompt and temperature per stage.
      </p>
      {state.stages.map((s, i) => (
        <div
          key={i}
          className="space-y-2 rounded-md border border-slate-200 p-3 dark:border-slate-700"
        >
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Input
                label={`Stage ${i + 1} name (snake_case)`}
                value={s.name}
                onChange={(e) => updateStage(i, { name: e.target.value })}
                placeholder="drafter"
              />
            </div>
            <div className="w-28">
              <Input
                label="Temperature"
                type="number"
                step={0.1}
                min={0}
                max={2}
                value={s.temperature}
                onChange={(e) =>
                  updateStage(i, { temperature: parseFloat(e.target.value) || 0 })
                }
              />
            </div>
            <Button variant="ghost" onClick={() => removeStage(i)}>
              <TrashIcon className="h-4 w-4" />
            </Button>
          </div>
          <Input
            label="Description (optional)"
            value={s.description}
            onChange={(e) => updateStage(i, { description: e.target.value })}
            placeholder="What this stage does"
          />
          <div>
            <label className="mb-1 block text-sm font-medium">Stage prompt (10+ chars)</label>
            <textarea
              rows={3}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 font-mono text-xs dark:border-slate-600 dark:bg-slate-800"
              value={s.prompt}
              onChange={(e) => updateStage(i, { prompt: e.target.value })}
              placeholder="You are the drafter. Write a first-pass draft of the content based on the input."
            />
          </div>
        </div>
      ))}
      <Button variant="secondary" onClick={addStage}>
        <PlusIcon className="mr-1 h-4 w-4" /> Add stage
      </Button>
    </div>
  )
}

// ── Step 9: Review ──

function Step9Review({
  state,
  validity,
}: {
  state: WizardState
  validity: Record<number, boolean>
}) {
  const step8Label =
    state.pattern === 'tier3_langgraph'
      ? 'Tier 3 actions'
      : state.pattern === 'tier3_crew'
      ? 'Tier 3 crew stages'
      : 'Pattern-specific config (not required)'
  const checklist = [
    { id: 1, label: 'Taxonomy (tier, type, variant, modalities)' },
    { id: 2, label: 'Code generation pattern' + (state.pattern === 'tier2_specialist' ? ' + output schema' : '') },
    { id: 3, label: 'Name, display name, description, domain, version' },
    { id: 4, label: 'System prompt + specialist type' },
    { id: 5, label: 'Tools declared' },
    { id: 6, label: 'Architecture membership' },
    { id: 7, label: 'Detailed docs (summary, input, output, use cases)' },
    { id: 8, label: step8Label },
  ]
  return (
    <div className="space-y-3">
      <div className="rounded-md bg-slate-50 px-3 py-2 text-sm dark:bg-slate-800">
        <div className="font-semibold">{state.display_name || '(unnamed)'}</div>
        <div className="text-xs text-slate-500">
          {state.name || 'no-name'} · tier {state.tier} · {state.agent_type} · {state.variant} ·{' '}
          {state.pattern}
        </div>
      </div>
      <div className="space-y-1">
        {checklist.map((c) => {
          const ok = validity[c.id]
          return (
            <div key={c.id} className="flex items-center gap-2 text-sm">
              {ok ? (
                <CheckCircleIcon className="h-5 w-5 text-emerald-500" />
              ) : (
                <XCircleIcon className="h-5 w-5 text-red-500" />
              )}
              <span className={ok ? 'text-slate-700 dark:text-slate-300' : 'text-red-600 dark:text-red-400'}>
                {c.label}
              </span>
            </div>
          )
        })}
      </div>
      <div className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-800 dark:bg-blue-950/40 dark:text-blue-300">
        <strong>After creation:</strong> the backend must be restarted for the new agent to be
        registered. The agent file will be written to{' '}
        <code>backend/agents/graphs/user/</code> and metadata to{' '}
        <code>backend/agents/user_agent_docs.py</code>.
      </div>
    </div>
  )
}

// ── Success view ──

function SuccessView({ result, onClose }: { result: any; onClose: () => void }) {
  return (
    <div className="space-y-4 text-center">
      <CheckCircleIcon className="mx-auto h-16 w-16 text-emerald-500" />
      <h3 className="text-lg font-semibold">Agent created successfully</h3>
      <div className="mx-auto max-w-md rounded-md bg-slate-50 p-3 text-left text-xs dark:bg-slate-800">
        <div>
          <strong>Name:</strong> {result.name}
        </div>
        <div>
          <strong>Class:</strong> {result.class_name}
        </div>
        <div>
          <strong>File:</strong> <code>{result.file_path}</code>
        </div>
        <div>
          <strong>Import:</strong> <code>{result.relative_import}</code>
        </div>
      </div>
      <div className="mx-auto max-w-md rounded-md border border-amber-300 bg-amber-50 p-3 text-left text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
        <strong>Restart required:</strong> {result.message}
      </div>
      <Button onClick={onClose}>Close</Button>
    </div>
  )
}

// ── Shared primitives ──

function Section({
  title,
  help,
  children,
}: {
  title: string
  help?: string
  children: ReactNode
}) {
  return (
    <div>
      <div className="mb-1 text-sm font-semibold text-slate-800 dark:text-slate-200">{title}</div>
      {help && <div className="mb-2 text-xs text-slate-500">{help}</div>}
      {children}
    </div>
  )
}

function RadioGroup({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: any; label: string; hint?: string; disabled?: boolean }>
  value: any
  onChange: (v: any) => void
}) {
  return (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
      {options.map((o) => {
        const active = o.value === value
        return (
          <button
            key={String(o.value)}
            onClick={() => !o.disabled && onChange(o.value)}
            disabled={o.disabled}
            className={clsx(
              'rounded-md border p-3 text-left transition-colors',
              o.disabled && 'cursor-not-allowed opacity-50',
              active
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/40'
                : 'border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-800',
            )}
          >
            <div className="text-sm font-semibold">{o.label}</div>
            {o.hint && <div className="mt-1 text-xs text-slate-500">{o.hint}</div>}
          </button>
        )
      })}
    </div>
  )
}
